import React from 'react';
import { FullPage, Grid } from '@dewise-devex/backstage-plugin-mui-uikit-react';
import {
  EntityListProvider,
  InspectEntityDialog,
  UnregisterEntityDialog,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import { entityWarningContent } from './DefaultEntityLayout';
import {
  EntityControls,
  useEntityControl,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { GenericError } from '../../errors/GenericError';
import { Entity } from '@backstage/catalog-model';

/**
 * Props for the FullPageEntityLayout component.
 */
export interface FullPageEntityLayoutProps {
  /**
   * The content of the layout.
   */
  children?: React.ReactNode;
}

export interface LayoutContentProps {
  entity: Entity;
  children: React.ReactNode;
}

const LayoutContent = (props: LayoutContentProps): React.ReactElement => {
  const {
    setUnregisterDialogOpen,
    setInspectDialogOpen,
    unregisterDialogOpen,
    handleInspectClose,
    handleUnregisterClose,
    handleUnregisterConfirm,
    inspectDialogOpen,
  } = useEntityControl(props.entity);

  return (
    <EntityListProvider>
      <FullPage
        variant="card"
        HeaderComponent={() => (
          <EntityControls
            entity={props.entity}
            setUnregisterDialogOpen={setUnregisterDialogOpen}
            setInspectDialogOpen={setInspectDialogOpen}
          />
        )}
      >
        <Grid container spacing={3}>
          {entityWarningContent}
          {props.children}
        </Grid>

        <UnregisterEntityDialog
          open={unregisterDialogOpen}
          onClose={handleUnregisterClose}
          entity={props.entity}
          onConfirm={handleUnregisterConfirm}
        />
        <InspectEntityDialog
          open={inspectDialogOpen}
          onClose={handleInspectClose}
          entity={props.entity}
        />
      </FullPage>
    </EntityListProvider>
  );
};

/**
 * A layout component for rendering a full-page entity view.
 *
 * The `FullPageEntityLayout` wraps entity-related content with dialogs for
 * inspecting and unregistering entities, as well as a header with entity controls.
 *
 * @param props - The properties of the component.
 * @returns The rendered `FullPageEntityLayout` component.
 *
 * @example
 * ```tsx
 * <FullPageEntityLayout>
 *   <CustomEntityContent />
 * </FullPageEntityLayout>
 * ```
 *
 * @category Entity
 */
export const FullPageEntityLayout = (
  props: FullPageEntityLayoutProps,
): JSX.Element => {
  const { entity } = useAsyncEntity();
  if (entity) {
    return <LayoutContent entity={entity}>{props.children}</LayoutContent>;
  }
  return <GenericError />;
};
